'use client';

import { clearErrors } from '@/redux/errorSlice';
import { pushStepVariables } from '@/redux/flowSlice';
import { RootState } from '@/redux/store';
import { Button, Stack } from '@mui/material';
import { onbCompleteFlow, steps, variables } from '@repo/onb-api';
import { DevOptionSelect, DevPanel as DevPanelShell, DevSectionTitle, DevToggleRow } from '@repo/ui';
import { deleteClientCookie, getClientCookie, setClientCookie } from '@repo/utils';
import { FORCE_MOCK_COOKIE, MOCK_CONFIG_COOKIE } from '@repo/utils/devTools';
import { useRouter } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  BIOMETRY_FLOWS,
  DEFAULT_MOCK_CONFIG,
  FORCE_AUTOCOMPLETE_COOKIE,
  IDENTITY_RESULTS,
  INSTANCE_RESULTS,
  MOCK_INSTANCE_ID,
  OFFERS,
  ORDER_CREDENTIALS,
  parseMockConfig,
  REJECTION_REASONS,
  START_STEPS,
  SUBMIT_RESULTS,
} from './config';

/**
 * Onboarding dev tools: on top of the shared panel, picks the step the mock instance resumes at and how the mock
 * backend answers submits, identity verification, the offer, instance creation and order credentials. Also fills the
 * current step with the mock flow's values, on demand or on every step, and bypasses Metamap.
 */
export const DevPanel = () => {
  const dispatch = useDispatch();
  const router = useRouter();
  const stepName = useSelector((state: RootState) => state.navigationdata.currentStep);
  const initialized = useSelector(
    (state: RootState) => state?.flowdata?.steps?.find((s) => s.name === stepName)?.initialized || false,
  );
  const [autocomplete, setAutocomplete] = useState(false);
  // Start step saved when the panel opened: applying a new one jumps to it
  const openedStartStep = useRef(DEFAULT_MOCK_CONFIG.startStep);

  const stepAutocomplete = () => {
    const mockStep = onbCompleteFlow.steps.find((s) => s.name === stepName);
    const mockVariables = Object.entries(mockStep?.variables || {}) as [keyof variables, string][];
    if (mockStep && mockVariables.length > 0) {
      mockVariables.forEach(([variable, value]) => {
        dispatch(pushStepVariables({ stepName: mockStep.name as keyof steps, variable, value }));
      });
    }
    dispatch(clearErrors());
  };

  useEffect(() => {
    setAutocomplete(getClientCookie(FORCE_AUTOCOMPLETE_COOKIE) === 'true');
  }, []);

  // Lives here, not in the drawer: it must fill each step as it initializes while the panel is closed
  useEffect(() => {
    if (initialized && autocomplete) stepAutocomplete();
  }, [autocomplete, initialized]);

  const toggleAutocomplete = (checked: boolean) => {
    setClientCookie(FORCE_AUTOCOMPLETE_COOKIE, String(checked));
    setAutocomplete(checked);
  };

  return (
    <DevPanelShell
      envForcesMock={process.env.NEXT_PUBLIC_ONB_API_FORCEMOCK === 'true'}
      envFlagName="NEXT_PUBLIC_ONB_API_FORCEMOCK"
      defaults={DEFAULT_MOCK_CONFIG}
      parse={parseMockConfig}
      failureHelper="Aplica a todas las llamadas: crear la instancia, inicializar y enviar pasos."
      renderScenarios={({ config, update, disabled }) => (
        <>
          <DevOptionSelect
            label="Retomar la instancia en"
            value={config.startStep}
            options={START_STEPS}
            disabled={disabled}
            onChange={(startStep) => update({ startStep })}
            helper="Al aplicar, abre /flow con la instancia mock en ese paso."
          />
          <DevOptionSelect
            label="Al enviar un paso"
            value={config.submitResult}
            options={SUBMIT_RESULTS}
            disabled={disabled}
            onChange={(submitResult) => update({ submitResult })}
            helper="El error de validación marca el primer campo del paso."
          />
          {config.submitResult === 'rejected' && (
            <DevOptionSelect
              label="Motivo del rechazo"
              value={config.rejectionReason}
              options={REJECTION_REASONS}
              disabled={disabled}
              onChange={(rejectionReason) => update({ rejectionReason })}
            />
          )}
          <DevOptionSelect
            label="Validación de identidad"
            value={config.identity}
            options={IDENTITY_RESULTS}
            disabled={disabled}
            onChange={(identity) => update({ identity })}
            helper="Resultado al enviar IDENTITY_VERIFICATION_START."
          />
          <DevOptionSelect
            label="Tipo de validación de identidad"
            value={config.biometry}
            options={BIOMETRY_FLOWS}
            disabled={disabled}
            onChange={(biometry) => update({ biometry })}
            helper="flowType de IDENTITY_VERIFICATION_START."
          />
          <DevOptionSelect
            label="Oferta aprobada"
            value={config.offer}
            options={OFFERS}
            disabled={disabled}
            onChange={(offer) => update({ offer })}
            helper="Monto máximo de la política (policyMaximumAmount)."
          />
          <DevOptionSelect
            label="Crear la instancia"
            value={config.instance}
            options={INSTANCE_RESULTS}
            disabled={disabled}
            onChange={(instance) => update({ instance })}
          />
          <DevOptionSelect
            label="Credencial de la orden (onb-cuotas)"
            value={config.orderCredential}
            options={ORDER_CREDENTIALS}
            disabled={disabled}
            onChange={(orderCredential) => update({ orderCredential })}
            helper="La que busca /flow?hash=… para iniciar el pago."
          />
        </>
      )}
      extras={
        <Stack sx={{ gap: 2 }}>
          <DevSectionTitle>Pasos</DevSectionTitle>
          <Button variant="outlined" onClick={stepAutocomplete}>
            Autocompletar paso
          </Button>
          <DevToggleRow
            id="dev-tools-autocomplete"
            label="Autocompletar siempre"
            hint="Completa cada paso con los datos del flujo mock apenas se inicializa."
            checked={autocomplete}
            onChange={toggleAutocomplete}
          />
          {stepName === 'IDENTITY_VERIFICATION_START' && (
            <Button variant="outlined" onClick={() => document.getElementById('metamapSubmit')?.click()}>
              Saltear Metamap
            </Button>
          )}
        </Stack>
      }
      onOpen={() => {
        openedStartStep.current = parseMockConfig(getClientCookie(MOCK_CONFIG_COOKIE)).startStep;
        setAutocomplete(getClientCookie(FORCE_AUTOCOMPLETE_COOKIE) === 'true');
      }}
      // A new start step resumes the mock instance there: /flow loads the instanceId cookie's instance
      onSave={() => {
        const { startStep } = parseMockConfig(getClientCookie(MOCK_CONFIG_COOKIE));
        const mockOn =
          process.env.NEXT_PUBLIC_ONB_API_FORCEMOCK === 'true' || getClientCookie(FORCE_MOCK_COOKIE) === 'true';
        if (!mockOn || startStep === 'default' || startStep === openedStartStep.current) return;
        if (!getClientCookie('instanceId')) setClientCookie('instanceId', MOCK_INSTANCE_ID);
        router.push('/flow');
      }}
      onReset={() => {
        deleteClientCookie(FORCE_AUTOCOMPLETE_COOKIE);
        setAutocomplete(false);
        if (getClientCookie('instanceId') === MOCK_INSTANCE_ID) deleteClientCookie('instanceId');
      }}
      // Left, as the WhatsApp button sits bottom right. Phones: above the step's bottom buttons, which span the width.
      // Desktop: bottom left, beside the step card
      fabSx={{ left: 16, bottom: { xs: 96, md: 16 } }}
    />
  );
};
